import { useState } from "react";
import { Phone, MessageCircle, CalendarCheck } from "lucide-react";
import { INFO, IMAGES, waLink } from "@/lib/data";
import { Reveal, MaskedLines } from "./motion";
import { toast } from "sonner";

const field =
  "w-full bg-transparent border-b border-charcoal/25 focus:border-terracotta outline-none py-3 font-sans text-charcoal placeholder:text-charcoal transition-colors duration-300";

// Reservation time slots every 15 minutes — al momento solo cena (19:00 – 23:45).
const TIME_SLOTS = (() => {
  const slots = [];
  for (let t = 1140; t <= 1425; t += 15) {
    const h = String(Math.floor(t / 60) % 24).padStart(2, "0");
    const m = String(t % 60).padStart(2, "0");
    slots.push(`${h}:${m}`);
  }
  return slots;
})();

// 1..15 then 15+
const PERSONE = [...Array(15)].map((_, i) => String(i + 1)).concat("15+");

// Data minima = oggi (fuso orario locale)
const TODAY = new Date().toLocaleDateString("en-CA");

const timeToMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export default function Reservations() {
  const [form, setForm] = useState({
    nome: "",
    telefono: "",
    data: "",
    ora: "",
    persone: "2",
    note: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Enforce "no past dates" on every device (some mobile pickers ignore `min`).
  const onDateChange = (e) => {
    const v = e.target.value;
    if (v && v < TODAY) {
      toast.error("Puoi prenotare solo a partire da oggi.");
      setForm((f) => ({ ...f, data: "" }));
      return;
    }
    setForm((f) => {
      // Se scelgo oggi e l'orario già selezionato è passato, lo azzero.
      const oraPassata = v === TODAY && f.ora && timeToMinutes(f.ora) <= nowMinutes;
      return { ...f, data: v, ora: oraPassata ? "" : f.ora };
    });
  };

  const dateLabel = form.data
    ? (() => {
        const d = new Date(`${form.data}T00:00:00`);
        const s = new Intl.DateTimeFormat("it-IT", {
          weekday: "long",
          day: "numeric",
          month: "long",
        }).format(d);
        return s.charAt(0).toUpperCase() + s.slice(1);
      })()
    : "";
  const personeLabel = `${form.persone} ${form.persone === "1" ? "persona" : "persone"}`;

  // Se la data scelta è oggi, mostra solo gli orari non ancora passati.
  const nowMinutes = (() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  })();
  const availableSlots =
    form.data === TODAY ? TIME_SLOTS.filter((t) => timeToMinutes(t) > nowMinutes) : TIME_SLOTS;

  const submit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono || !form.data || !form.ora) {
      toast.error("Compila nome, telefono, data e orario.");
      return;
    }
    if (form.data < TODAY) {
      toast.error("La data scelta è già passata. Seleziona oggi o un giorno futuro.");
      return;
    }
    if (form.data === TODAY && timeToMinutes(form.ora) <= nowMinutes) {
      toast.error("L'orario scelto è già passato. Seleziona un orario successivo o un altro giorno.");
      return;
    }
    const text =
      `Ciao Bar Ristorante Gabri! Vorrei prenotare un tavolo per la cena.\n\n` +
      `Nome: ${form.nome}\n` +
      `Telefono: ${form.telefono}\n` +
      `Data: ${dateLabel}\n` +
      `Orario: ${form.ora}\n` +
      `Persone: ${personeLabel}` +
      (form.note ? `\nNote: ${form.note}` : "");
    window.open(waLink(text), "_blank");
    toast.success("Ti reindirizzo su WhatsApp per confermare la prenotazione.");
  };

  return (
    <section id="prenota" className="relative bg-olive text-bone py-24 sm:py-32 overflow-hidden grain">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Left: pitch + image */}
          <div>
            <span className="text-xs tracking-[0.25em] uppercase font-sans font-semibold text-bone/70">
              Prenotazioni
            </span>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              <MaskedLines lines={["Prenota il", "tuo tavolo"]} />
              <MaskedLines lines={["sul mare."]} lineClassName="italic" delay={0.1} />
            </h2>
            <p className="mt-6 font-sans text-bone/80 max-w-md leading-relaxed">
              Al momento la prenotazione è disponibile solo per la <strong className="text-bone">cena, dalle 19:00</strong>. Compila il modulo: invieremo i dettagli su WhatsApp per una conferma rapida. Preferisci il telefono? Chiamaci, rispondiamo subito.
            </p>

            <div className="mt-8">
              <a
                href={`tel:${INFO.phoneTel}`}
                data-testid="reservation-call-btn"
                className="inline-flex items-center justify-center gap-2 bg-bone text-charcoal px-8 py-4 rounded-sm font-semibold tracking-wide hover:bg-white transition-colors duration-300"
              >
                <Phone size={18} /> Chiama {INFO.phoneDisplay}
              </a>
              <p className="mt-4 text-sm text-bone/70 font-sans">
                Preferisci scrivere?{" "}
                <a
                  href={waLink("Ciao Bar Ristorante Gabri! Vorrei informazioni per una prenotazione.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reservation-whatsapp-direct"
                  className="inline-flex items-center gap-1 text-bone underline underline-offset-4 hover:text-white transition-colors duration-300"
                >
                  <MessageCircle size={14} /> Scrivici su WhatsApp
                </a>
              </p>
            </div>

            <Reveal delay={0.1} className="mt-10 overflow-hidden rounded-sm hidden lg:block">
              <img
                src={IMAGES.table}
                alt="Tavolo sul dehors del Bar Gabri con linguine ai frutti di mare"
                className="w-full h-56 object-cover"
              />
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="whatsapp-reservation-form"
              className="bg-bone text-charcoal rounded-sm p-7 sm:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
                <div className="sm:col-span-2">
                  <label htmlFor="res-nome" className="text-xs tracking-wide uppercase text-pebble font-sans">Nome e cognome</label>
                  <input id="res-nome" data-testid="res-nome" className={field} value={form.nome} onChange={set("nome")} placeholder="Il tuo nome" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="res-telefono" className="text-xs tracking-wide uppercase text-pebble font-sans">Telefono</label>
                  <input id="res-telefono" data-testid="res-telefono" className={field} value={form.telefono} onChange={set("telefono")} placeholder="Es. 333 123 4567" inputMode="tel" />
                </div>
                <div className="relative">
                  <label htmlFor="res-data" className="text-xs tracking-wide uppercase text-pebble font-sans">Data</label>
                  <input
                    id="res-data"
                    data-testid="res-data"
                    type="date"
                    aria-label="Data della prenotazione"
                    min={TODAY}
                    onClick={(e) => {
                      try {
                        e.currentTarget.showPicker && e.currentTarget.showPicker();
                      } catch (err) {
                        // showPicker() non è supportato ovunque: si ignora e resta il picker nativo.
                      }
                    }}
                    className={`${field} text-left cursor-pointer ${form.data ? "" : "text-transparent date-empty"}`}
                    value={form.data}
                    onChange={onDateChange}
                  />
                  {!form.data && (
                    <span className="pointer-events-none absolute left-0 bottom-3 font-sans text-charcoal">
                      Seleziona
                    </span>
                  )}
                </div>
                <div>
                  <label htmlFor="res-ora" className="text-xs tracking-wide uppercase text-pebble font-sans">Orario</label>
                  <select id="res-ora" data-testid="res-ora" className={field} value={form.ora} onChange={set("ora")}>
                    <option value="">Seleziona</option>
                    {availableSlots.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="res-persone" className="text-xs tracking-wide uppercase text-pebble font-sans">Numero persone</label>
                  <select id="res-persone" data-testid="res-persone" className={field} value={form.persone} onChange={set("persone")}>
                    {PERSONE.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="res-note" className="text-xs tracking-wide uppercase text-pebble font-sans">Note (facoltative)</label>
                  <input id="res-note" data-testid="res-note" className={field} value={form.note} onChange={set("note")} placeholder="Allergie, seggiolone, tavolo all'aperto…" />
                </div>
              </div>

              {form.data && form.ora && (
                <div
                  data-testid="reservation-recap"
                  className="mt-6 flex items-start gap-3 rounded-sm border border-olive/25 bg-olive/[0.06] px-4 py-3.5"
                >
                  <CalendarCheck size={18} className="text-olive mt-0.5 shrink-0" />
                  <p className="font-sans text-sm text-charcoal leading-relaxed">
                    <span className="font-semibold">Cena</span>
                    <span className="text-pebble"> · </span>
                    {dateLabel}
                    <span className="text-pebble"> · </span>
                    ore {form.ora}
                    <span className="text-pebble"> · </span>
                    {personeLabel}
                  </p>
                </div>
              )}

              <button
                type="submit"
                data-testid="res-submit-btn"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-dark text-bone py-4 rounded-sm font-semibold tracking-wide transition-colors duration-300"
              >
                <MessageCircle size={18} /> Invia richiesta su WhatsApp
              </button>
              <p className="mt-3 text-center text-xs text-pebble font-sans">
                Nessun account necessario · conferma diretta con il ristorante
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
